import { decompress } from "fzstd";
/**
 * Browsers refuse cursor images past roughly 128x128 (and Firefox ignores the
 * whole declaration rather than clamping), so oversized shapes are scaled down
 * and the hotspot scaled with them.
 */
const MAX_DIM = 128;
export class CursorRenderer {
    #cache = new Map();
    /**
     * Every element the remote pointer should appear on.
     *
     * Usually one canvas, but a view showing several screens at once has one per
     * screen. The pointer is only ever over one of them, so applying the same
     * shape to all of them shows it in the right place.
     */
    #targets = [];
    #current = null;
    #visible = true;
    constructor(target) {
        this.#targets = target ? [target] : [];
    }
    setTarget(target) {
        this.setTargets(target ? [target] : []);
    }
    setTargets(targets) {
        // Leave anything dropped without a cursor rule of our making.
        for (const el of this.#targets) {
            if (!targets.includes(el))
                el.style.cursor = "";
        }
        this.#targets = targets.filter((t) => !!t);
        this.#apply();
    }
    /** Stores a new shape and makes it current. */
    async update(data) {
        if (!data.width || !data.height)
            return;
        const expected = data.width * data.height * 4;
        // The peer zstd-compresses the RGBA (hbb_common::compress), so a 32x32
        // cursor arrives as a few hundred bytes, not 4096. Treating the short
        // buffer as malformed is why no cursor appeared at all.
        let colors = data.colors;
        if (colors.length !== expected) {
            try {
                colors = decompress(colors);
            }
            catch {
                return; // neither raw nor valid zstd; keep the previous shape
            }
            if (colors.length < expected)
                return;
        }
        const shape = await this.#build({ ...data, colors });
        if (!shape)
            return;
        this.#cache.set(String(data.id), shape);
        this.#current = shape;
        this.#apply();
    }
    /** Switches to a previously sent shape. */
    use(id) {
        const shape = this.#cache.get(String(id));
        if (!shape)
            return; // not seen yet; the peer will resend it
        this.#current = shape;
        this.#apply();
    }
    /** Hides the pointer entirely, e.g. while input is disabled. */
    setVisible(visible) {
        this.#visible = visible;
        this.#apply();
    }
    reset() {
        this.#cache.clear();
        this.#current = null;
        this.#apply();
    }
    async #build(data) {
        const { width, height } = data;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        const outW = Math.max(1, Math.round(width * scale));
        const outH = Math.max(1, Math.round(height * scale));
        try {
            const src = document.createElement("canvas");
            src.width = width;
            src.height = height;
            const sctx = src.getContext("2d");
            if (!sctx)
                return null;
            // Copy into a fresh buffer: ImageData will not take a view that might be
            // backed by shared memory.
            const rgba = new Uint8ClampedArray(width * height * 4);
            rgba.set(data.colors.subarray(0, rgba.length));
            sctx.putImageData(new ImageData(rgba, width, height), 0, 0);
            let out = src;
            if (scale < 1) {
                const dst = document.createElement("canvas");
                dst.width = outW;
                dst.height = outH;
                const dctx = dst.getContext("2d");
                if (!dctx)
                    return null;
                dctx.drawImage(src, 0, 0, outW, outH);
                out = dst;
            }
            const url = out.toDataURL("image/png");
            const hx = Math.round(data.hotx * scale);
            const hy = Math.round(data.hoty * scale);
            return {
                id: data.id,
                hotx: hx,
                hoty: hy,
                width: outW,
                height: outH,
                // `auto` is the fallback if the image fails to load.
                css: `url(${url}) ${hx} ${hy}, auto`,
            };
        }
        catch {
            return null;
        }
    }
    #apply() {
        // Until the peer sends a shape, show nothing rather than the browser's own
        // arrow, which would read as a second, wrong pointer.
        const css = this.#visible && this.#current ? this.#current.css : "none";
        for (const el of this.#targets)
            el.style.cursor = css;
    }
}
